import type { TTaskListItem } from '@modules/projects/tasks/types';
import {
  EGateDecision,
  EStageStatus,
  type TGateState,
  type TProjectStagesMock,
  type TStage,
  type TStageSection,
} from './types';

const getDateInDays = (days?: number): string | undefined => {
  if (typeof days !== 'number') return undefined;
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

export const getProjectStages = (projectStages: TProjectStagesMock[] | undefined, projectId: string): TStage[] =>
  (projectStages?.find(({ project }) => project === projectId)?.stages ?? []).map(
    ({ startInDays, endInDays, ...stage }, index) => ({
      ...stage,
      index,
      startDate: getDateInDays(startInDays),
      endDate: getDateInDays(endInDays),
    })
  );

/** A task joins the first stage listing its title; the tasks no stage lists come last. */
export const groupTasksByStage = (stages: TStage[], tasks: TTaskListItem[]): TStageSection[] => {
  const sections: TStageSection[] = stages.map((stage) => ({ stage, tasks: [] }));
  const otherTasks: TTaskListItem[] = [];
  tasks.forEach((task) => {
    const section = sections.find(({ stage }) => stage?.tasks?.includes(task.title));
    (section?.tasks ?? otherTasks).push(task);
  });
  return otherTasks.length ? [...sections, { tasks: otherTasks }] : sections;
};

export const isChecklistComplete = (stage: TStage): boolean =>
  (stage.gate?.checklist ?? []).every((_, index) => stage.gateState?.checked.includes(index));

/** Accept completes the stage and starts the next one; Cancel cancels the stage. */
export const updateStageGate = (stages: TStage[], stageIndex: number, changes: Partial<TGateState>): TStage[] => {
  const stage = stages[stageIndex];
  if (!stage?.gate || stage.status !== EStageStatus.CURRENT) return stages;
  const updated = { ...stage, gateState: { checked: [], ...stage.gateState, ...changes } };
  const isAccepted = changes.decision === EGateDecision.ACCEPT;
  if (isAccepted && !isChecklistComplete(updated)) return stages;
  if (isAccepted) updated.status = EStageStatus.COMPLETED;
  if (changes.decision === EGateDecision.CANCEL) updated.status = EStageStatus.CANCELLED;
  return stages.map((item, index) => {
    if (index === stageIndex) return updated;
    if (isAccepted && index === stageIndex + 1 && item.status === EStageStatus.UPCOMING)
      return { ...item, status: EStageStatus.CURRENT };
    return item;
  });
};
